import { clamp, UIRenderer, Enum, _decorator, Vec3, Vec4 } from 'cc';
import ShaderManipulator from './ShaderManipulator';

const { ccclass, property, requireComponent, executeInEditMode } = _decorator;
const tempVec4 = new Vec4();

export enum ColorTone {
    Shadow,
    Midtone,
    Highlight,
}

@ccclass('ColorBalanceAdjuster')
@requireComponent(UIRenderer)
@executeInEditMode
export default class ColorBalanceAdjuster extends ShaderManipulator {
    @property
    private _tone: ColorTone = ColorTone.Midtone;

    @property
    private _shadowBalance: Vec3 = new Vec3();

    @property
    private _midtoneBalance: Vec3 = new Vec3();

    @property
    private _highlightBalance: Vec3 = new Vec3();

    @property
    private _keepLuminance: boolean = true;

    @property({ type: Enum(ColorTone) })
    public set tone(value: ColorTone) {
        if (this._tone === value) {
            return;
        }
        this._tone = value;
    }
    public get tone() {
        return this._tone;
    }

    @property({
        min: -100,
        max: 100,
        step: 1,
        slide: true,
        visible() {
            return this.tone === ColorTone.Shadow;
        },
    })
    public set shadowRedBalance(value: number) {
        if (this._shadowBalance.x === value) {
            return;
        }
        this._shadowBalance.x = value;
        this.syncShadowBalanceProperty();
    }
    public get shadowRedBalance(): number {
        return this._shadowBalance.x;
    }

    @property({
        min: -100,
        max: 100,
        step: 1,
        slide: true,
        visible() {
            return this.tone === ColorTone.Shadow;
        },
    })
    public set shadowGreenBalance(value: number) {
        if (this._shadowBalance.y === value) {
            return;
        }
        this._shadowBalance.y = value;
        this.syncShadowBalanceProperty();
    }
    public get shadowGreenBalance(): number {
        return this._shadowBalance.y;
    }

    @property({
        min: -100,
        max: 100,
        step: 1,
        slide: true,
        visible() {
            return this.tone === ColorTone.Shadow;
        },
    })
    public set shadowBlueBalance(value: number) {
        if (this._shadowBalance.z === value) {
            return;
        }
        this._shadowBalance.z = value;
        this.syncShadowBalanceProperty();
    }
    public get shadowBlueBalance(): number {
        return this._shadowBalance.z;
    }

    @property({
        min: -100,
        max: 100,
        step: 1,
        slide: true,
        visible() {
            return this.tone === ColorTone.Midtone;
        },
    })
    public set midtoneRedBalance(value: number) {
        if (this._midtoneBalance.x === value) {
            return;
        }
        this._midtoneBalance.x = value;
        this.syncMidtoneBalanceProperty();
    }
    public get midtoneRedBalance(): number {
        return this._midtoneBalance.x;
    }

    @property({
        min: -100,
        max: 100,
        step: 1,
        slide: true,
        visible() {
            return this.tone === ColorTone.Midtone;
        },
    })
    public set midtoneGreenBalance(value: number) {
        if (this._midtoneBalance.y === value) {
            return;
        }
        this._midtoneBalance.y = value;
        this.syncMidtoneBalanceProperty();
    }
    public get midtoneGreenBalance(): number {
        return this._midtoneBalance.y;
    }

    @property({
        min: -100,
        max: 100,
        step: 1,
        slide: true,
        visible() {
            return this.tone === ColorTone.Midtone;
        },
    })
    public set midtoneBlueBalance(value: number) {
        if (this._midtoneBalance.z === value) {
            return;
        }
        this._midtoneBalance.z = value;
        this.syncMidtoneBalanceProperty();
    }
    public get midtoneBlueBalance(): number {
        return this._midtoneBalance.z;
    }

    @property({
        min: -100,
        max: 100,
        step: 1,
        slide: true,
        visible() {
            return this.tone === ColorTone.Highlight;
        },
    })
    public set highlightRedBalance(value: number) {
        if (this._highlightBalance.x === value) {
            return;
        }
        this._highlightBalance.x = value;
        this.syncHighlightBalanceProperty();
    }
    public get highlightRedBalance(): number {
        return this._highlightBalance.x;
    }

    @property({
        min: -100,
        max: 100,
        step: 1,
        slide: true,
        visible() {
            return this.tone === ColorTone.Highlight;
        },
    })
    public set highlightGreenBalance(value: number) {
        if (this._highlightBalance.y === value) {
            return;
        }
        this._highlightBalance.y = value;
        this.syncHighlightBalanceProperty();
    }

    public get highlightGreenBalance(): number {
        return this._highlightBalance.y;
    }

    @property({
        min: -100,
        max: 100,
        step: 1,
        slide: true,
        visible() {
            return this.tone === ColorTone.Highlight;
        },
    })
    public set highlightBlueBalance(value: number) {
        if (this._highlightBalance.z === value) {
            return;
        }
        this._highlightBalance.z = value;
        this.syncHighlightBalanceProperty();
    }
    public get highlightBlueBalance(): number {
        return this._highlightBalance.z;
    }

    @property
    public get keepLuminance(): boolean {
        return this._keepLuminance;
    }
    public set keepLuminance(value: boolean) {
        if (this._keepLuminance === value) {
            return;
        }
        this._keepLuminance = value;
        this.syncLuminanceProperty();
    }

    public get shadowBalance(): Readonly<Vec3> {
        return this._shadowBalance;
    }
    public set shadowBalance(value: Vec3) {
        if (this._shadowBalance.equals(value)) {
            return;
        }
        // Ensure the value is within the range of -100 to 100
        value.x = clamp(value.x, -100, 100);
        value.y = clamp(value.y, -100, 100);
        value.z = clamp(value.z, -100, 100);
        this._shadowBalance.set(value);
        this.syncShadowBalanceProperty();
    }

    public get midtoneBalance(): Readonly<Vec3> {
        return this._midtoneBalance;
    }
    public set midtoneBalance(value: Vec3) {
        if (this._midtoneBalance.equals(value)) {
            return;
        }
        this._midtoneBalance.set(value);
        this.syncMidtoneBalanceProperty();
    }

    public get highlightBalance(): Readonly<Vec3> {
        return this._highlightBalance;
    }
    public set highlightBalance(value: Vec3) {
        if (this._highlightBalance.equals(value)) {
            return;
        }
        this._highlightBalance.set(value);
        this.syncHighlightBalanceProperty();
    }

    protected syncProperty(): void {
        this.syncShadowBalanceProperty();
        this.syncMidtoneBalanceProperty();
        this.syncHighlightBalanceProperty();
        this.syncLuminanceProperty();
    }

    protected syncShadowBalanceProperty() {
        const pass = this.targetPass;
        if (!pass) {
            return;
        }
        // Normalize the shadowBalance values to the range of -1 to 1
        pass.setUniform(
            pass.getHandle('shadowBalance'),
            tempVec4.set(this.shadowBalance.x, this.shadowBalance.y, this.shadowBalance.z, 0).multiplyScalar(1 / 100),
        );
    }

    protected syncMidtoneBalanceProperty() {
        const pass = this.targetPass;
        if (!pass) {
            return;
        }
        // Normalize the midtoneBalance values to the range of -1 to 1
        pass.setUniform(
            pass.getHandle('midtoneBalance'),
            tempVec4.set(this.midtoneBalance.x, this.midtoneBalance.y, this.midtoneBalance.z, 0).multiplyScalar(1 / 100),
        );
    }

    protected syncHighlightBalanceProperty() {
        const pass = this.targetPass;
        if (!pass) {
            return;
        }
        // Normalize the highlightBalance values to the range of -1 to 1
        pass.setUniform(
            pass.getHandle('highlightBalance'),
            tempVec4.set(this.highlightBalance.x, this.highlightBalance.y, this.highlightBalance.z, 0).multiplyScalar(1 / 100),
        );
    }

    protected syncLuminanceProperty() {
        const pass = this.targetPass;
        if (!pass) {
            return;
        }
        pass.setUniform(pass.getHandle('keepLuminance'), this.keepLuminance ? 1 : 0);
    }
}
